import { faPlaneArrival, faPlaneDeparture } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Typography, useTheme } from '@mui/material'
import { Divider, Grid, Stack } from '@vapor/material'
import { useEffect, useMemo } from 'react'
import { FieldError } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { compareDates } from 'src/utils/date'
import { HiCIcon } from '../../../../../components/icons/icon'
import { CheckoutAmountsSchema, CheckoutDepositsSchema } from '../checkout-document-form.schema'
import { getFiscalSum, getNonFiscalSum, getProductionAssignedTotal, reduceProductionAssigneds } from '../checkout-document-form.utils'
import { CoData } from './co-query-wrapper'
import { formatResource } from 'src/utils/resource-type'

export type CoAmountsError = {
  billsAmount?: FieldError | undefined
  fiscalDepositsAmount?: FieldError | undefined
  totalAmount?: FieldError | undefined
  notFiscalDepositsAmount?: FieldError | undefined
  paymentAmount?: FieldError | undefined
}

export type CoAmountsProps = {
  onChange: (value: CheckoutAmountsSchema) => void
  queryData: CoData
  formDeposits: CheckoutDepositsSchema
  error?: CoAmountsError
}

export default function CoAmounts(props: CoAmountsProps) {
  const { onChange, queryData, formDeposits, error } = props
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { formatCurrency } = useFormatCurrency()
  const localizeString = useLocalizeString()
  const formatDate = useFormatDate()
  const theme = useTheme()

  const amounts: CheckoutAmountsSchema = useMemo(() => {
    const billsAmount = getBillsAmount(queryData)
    const fiscalDepositsAmount = getFiscalSum(formDeposits ?? [])
    const totalAmount = billsAmount - fiscalDepositsAmount
    const notFiscalDepositsAmount = getNonFiscalSum(formDeposits ?? [])
    const paymentAmount = totalAmount - notFiscalDepositsAmount
    return { billsAmount, fiscalDepositsAmount, totalAmount, notFiscalDepositsAmount, paymentAmount }
  }, [queryData, formDeposits])

  useEffect(() => onChange(amounts), [amounts])

  const reservation = useMemo(() => {
    if (queryData.reservations.length === 1) {
      return queryData.reservations[0]
    }
    return null
  }, [queryData.reservations])

  const reservationDateRange = useMemo(() => {
    if (!reservation?.reservedResources || reservation.reservedResources.length <= 0) {
      return null
    }
    const start = formatDate(reservation.reservedResources.map((rr) => rr.start).sort((a, b) => compareDates(a, b))[0])
    const end = formatDate(reservation.reservedResources.map((rr) => rr.end).sort((a, b) => -compareDates(a, b))[0])
    return { start, end }
  }, [reservation])

  const resource = useMemo(() => {
    if (!reservation || !reservation.reservedResources || reservation.reservedResources.length > 1) {
      return null
    }
    const ro = reservation?.reservedResources?.[0]?.resourceOccupancies?.[0]
    if (!ro) {
      return null
    }
    return formatResource(localizeString, ro.reservedResourceType, ro.resource)
  }, [reservation, localizeString])

  const unpaidAmount = useMemo(() => {
    return queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE' ? queryData.document?.unpaidAmount : 0
  }, [queryData])

  return (
    <>
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" spacing={1}>
            <Stack direction="column" spacing={1}>
              <Typography //
                component="span"
                color={error?.billsAmount || error?.totalAmount ? theme.palette.error.main : undefined}
              >
                {t('pms.common.checkout_document.amounts.total')}
              </Typography>
              <Typography //
                component="span"
                color={error?.billsAmount || error?.totalAmount ? theme.palette.error.main : undefined}
              >
                <strong>{formatCurrency(amounts.totalAmount, currency)}</strong>
              </Typography>
            </Stack>
            <Stack direction="column" spacing={1}>
              <Typography //
                component="span"
                color={error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                {t('pms.common.checkout_document.amounts.payment')}
              </Typography>
              <Typography //
                component="div"
                color={error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                <strong>{formatCurrency(amounts.paymentAmount, currency)}</strong>
              </Typography>
            </Stack>
            {unpaidAmount > 0 && (
              <Stack direction="column" spacing={1}>
                <Typography //
                  component="span"
                  color={theme.palette.error.main}
                >
                  {t('pms.common.checkout_document.amounts.unpaid_amount')}
                </Typography>
                <Typography //
                  component="div"
                  color={theme.palette.error.main}
                >
                  <strong>{formatCurrency(unpaidAmount, currency)}</strong>
                </Typography>
              </Stack>
            )}
          </Stack>

          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Typography //
                component="span"
                color={error?.fiscalDepositsAmount || error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                {t('pms.common.checkout_document.amounts.advances_fiscal')}
              </Typography>
              <Typography //
                component="span"
                color={error?.fiscalDepositsAmount || error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                <strong>{formatCurrency(amounts.fiscalDepositsAmount, currency)}</strong>
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Typography //
                component="span"
                color={error?.notFiscalDepositsAmount || error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                {t('pms.common.checkout_document.amounts.advances_not_fiscal')}
              </Typography>
              <Typography //
                component="span"
                color={error?.notFiscalDepositsAmount || error?.paymentAmount ? theme.palette.error.main : undefined}
              >
                <strong>{formatCurrency(amounts.notFiscalDepositsAmount, currency)}</strong>
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Grid>
      {reservation !== null && (
        <Grid item xs={12}>
          <Stack direction="row" justifyContent="space-between">
            <Stack direction="row" spacing={1}>
              <div>
                <Typography component="span" variant="caption">
                  {t('pms.common.checkout_document.amounts.reservation')}{' '}
                </Typography>
                <Typography component="span" variant="caption">
                  <strong>{reservation.reservationCode}</strong>
                </Typography>
              </div>

              {resource !== null && (
                <div>
                  <Typography component="span" variant="caption">
                    {t('pms.common.checkout_document.amounts.resource')}{' '}
                  </Typography>
                  <Typography component="span" variant="caption">
                    <strong>{resource}</strong>
                  </Typography>
                </div>
              )}
            </Stack>
            {reservationDateRange !== null && (
              <Stack direction="row" alignItems="center">
                <HiCIcon>
                  <FontAwesomeIcon icon={faPlaneArrival} />
                </HiCIcon>
                <Typography variant="caption" mx={1}>
                  {reservationDateRange.start}
                </Typography>
                <HiCIcon>
                  <FontAwesomeIcon icon={faPlaneDeparture} />
                </HiCIcon>
                <Typography variant="caption" mx={1}>
                  {reservationDateRange.end}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Grid>
      )}
      <Grid item xs={12}>
        <Divider light />
      </Grid>
    </>
  )
}

function getBillsAmount(queryData: CoData): number {
  if (queryData.mode === 'CREATE') {
    return getProductionAssignedTotal(reduceProductionAssigneds(queryData.bills))
  } else {
    if ((queryData.mode === 'READ_ONLY' || queryData.mode === 'UPDATE') && queryData.document.fiscalType === 'PAID_IN_FULL') return queryData.document.totalAmount
    return queryData.document.documentDetails //
      .filter((d) => !d.referenceDocument)
      .reduce((p, d) => p + d.totalAmount, 0)
  }
}
